import React, {useCallback, useRef} from "react";
import { FiltersBar } from "~/apps/core/ui/FiltersBar";
import {SearchAppBar} from "~/apps/core/ui/SearchAppBar";
import {Box, Button} from "@mui/material";
import {useHistoryState} from "~/apps/core/utils";
import {tuple} from "~/types";
import {useHistory} from "react-router-dom";
import {PassTypeChipInput, PassTypeChipInputProps} from "~/apps/packages/passes/components/PassTypeChipInput";
import {AdmissionList} from "~/apps/packages/passes/components/AdmissionList/AdmissionList";
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {AddCircle} from "@mui/icons-material";
import {
    AdmissionStatesChipInput,
    AdmissionStatesChipInputProps
} from "~/apps/packages/passes/components/AdmissionStatusChipInput";
import {Admission} from "~/queries";
import {AbsoluteContainer} from "~/apps/core/ui/AbsoluteContainer";
import {useChangeEffect} from "~/lib/useChangeEffect";


type FilterState = {
    search?: string
    type?: any
    statuses?: Admission["status"][]
}


const useFilters = () => {
    const [value, setValue, delValue] = useHistoryState<FilterState>({
        name: "filter",
        handleBack: false,
        serialize: state => JSON.stringify(state),
        deserialize: data => {
            const raw = JSON.parse(data);
            const value: FilterState = {};
            if (typeof raw?.search == "string" && raw.search.length > 0) {
                value.search = raw.search;
            }
            if (typeof raw?.type == "string" && raw.type.length > 0) {
                value.type = raw.type;
            }
            if (Array.isArray(raw?.statuses) && raw.statuses.length > 0) {
                value.statuses = raw.statuses;
            }
            return value;
        }
    });

    const setFilter = useCallback(async value => {
        if (value) {
            value = {...value};
            if (!value.search) {
                delete value.search;
            }
            if (!value.type) {
                delete value.type;
            }
            if (!value.statuses || value.statuses.length == 0){
                delete value.statuses;
            }
            if (Object.keys(value).length > 0) {
                setValue(value);
                return;
            }
        }
        await delValue();
    }, []);

    return tuple(value, setFilter);
}


export const IndexPage = () => {
    const $container = useRef<HTMLDivElement>();
    const history = useHistory();
    const [filter, setFilter] = useFilters();
    const title = filter?.search || "Заявки на пропуск";

    useChangeEffect(() => {
        $container.current?.scrollTo({top: 0});
    }, [filter]);

    const handleSearchQueryChange = (query) => {
        setFilter({
            ...filter,
            search: query
        }).then(() => null);
    }

    const handleTypeChange: PassTypeChipInputProps["onChange"] = (value) => {
        setFilter({
            ...filter,
            type: value
        }).then(() => null);
    }

    const handleStatusChange: AdmissionStatesChipInputProps["onChange"] = (value) => {
        setFilter({
            ...filter,
            statuses: value
        }).then(() => null);
    }

    const handleCreateClick = () => {
        history.push("/new");
    }

    return (
        <>
            <SearchAppBar
                position="fixed"
                color="default"
                title={title}
                onSearch={handleSearchQueryChange}
                query={filter?.search}
            >
                <FiltersBar>
                    <PassTypeChipInput
                        value={filter?.type}
                        onChange={handleTypeChange}
                    />
                    <AdmissionStatesChipInput value={filter?.statuses} onChange={handleStatusChange}/>
                </FiltersBar>
            </SearchAppBar>
            <AbsoluteContainer disableGutters top={104} ref={$container}>
                <AdmissionList query={filter?.search} type={filter?.type} statuses={filter?.statuses}/>
                <Box minHeight="80px"/>
                <FloatingActions>
                    <Button fullWidth onClick={handleCreateClick} color="primary" startIcon={<AddCircle/>}>
                        Создать заявку
                    </Button>
                </FloatingActions>
            </AbsoluteContainer>
        </>
    );
}